import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonCopy } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { cloneDeep, partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  lessonToCopy: CardStack
  cardStackId: string
  currentUserId: string
  ownerIds: Array<string>
  planbookId: string
  courseId: string
}

export default function copyLessonAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_COPY",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  // See PLANBOOK_COPY_ALL_LESSONS_FOR_DAY
  // IF use this again, abstract!
  let lessonAttributes = cloneDeep(payload.lessonToCopy.attributes)
  lessonAttributes.parentCardStack = {
    id: payload.lessonToCopy.id,
    version: payload.lessonToCopy.attributes.version,
    ownerIds: payload.ownerIds,
    courseId: payload.courseId,
    planbookId: payload.planbookId,
    source: "paste",
  }
  if (lessonAttributes.cards) {
    for (const card of lessonAttributes.cards) {
      card.attributes["commentIds"] = []
    }
  }

  let patch = Patch.create("user", payload.currentUserId).set(
    "attributes.clipboard.cardStackAttributes",
    lessonAttributes,
  ).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let models = defaultLessonModels(payload, findFn, userId)
  let cardStackTitle = models.cardStack ? models.cardStack.attributes.title : null

  let newEvent: LessonCopy = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.courseId, payload.cardStackId],
    primary_verb: "copy",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
    primary_object_type: "lesson",
    primary_object_specifier: null,

    adverbial_verb: null,
    adverbial_object_type: null,
    adverbial_object_specifier: null,
    adverbial_object_id: null,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: [
      payload.planbookId,
      payload.courseId,
      payload.cardStackId,
    ],
  }

  return {
    title: "Lesson Copied",
    titleForAnalytics: "Lesson - Copy",
    description: `Copied "${cardStackTitle}"`,
    context: defaultLessonContext(payload, models),
    activity: null,
    newAnalyticsEvents: [newEvent],
  }
}
